/**
 * 화이트보드 공통 훅: useFullscreen, useWhiteboardTools, useWhiteboardZoom
 * v1 원본: utils/whiteboard/useWhiteboardCommon.js (300줄)
 */
import { useState, useEffect, useCallback, useRef } from 'react';

// ─── useFullscreen ─────────────────────────────────────────────────────────

export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
    };
  }, []);

  const toggleFullscreen = useCallback((elementId = 'fileViewer') => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      const el = document.getElementById(elementId) ?? document.documentElement;
      el.requestFullscreen?.();
    }
  }, []);

  return { isFullscreen, toggleFullscreen };
}

// ─── useWhiteboardTools ────────────────────────────────────────────────────

export interface ExcalidrawAPI {
  getAppState: () => Record<string, unknown> & { activeTool: { type: string } };
  setActiveTool: (tool: { type: string }) => void;
  updateScene: (scene: { appState?: Record<string, unknown> }) => void;
  getSceneElements: () => unknown[];
  scrollToContent: (elements?: unknown[], opts?: { fitToContent?: boolean; fitToViewport?: boolean }) => void;
  getFiles: () => Record<string, { id: string; dataURL: string }>;
  addFiles: (files: unknown[]) => void;
  undo: () => void;
  redo: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  onChange: (cb: (elements: unknown[], appState: Record<string, unknown>) => void) => void;
}

export function useWhiteboardTools(excalidrawApi: React.RefObject<ExcalidrawAPI | null>) {
  const [currentTool, setCurrentTool] = useState<string>('selection');
  const [isOpenDiagramPopover, setIsOpenDiagramPopover] = useState(false);

  const onClickTools = useCallback((type: string) => {
    if (!excalidrawApi.current) return;

    // 도형 팝업이 열려있으면 닫기 (워크플로우 1: 도형 선택 시, 워크플로우 3: 다른 도구 선택 시)
    setIsOpenDiagramPopover(false);

    const activeTool = excalidrawApi.current.getAppState().activeTool.type;
    if (activeTool === type) {
      if (type === 'freedraw') {
        // 펜은 재클릭 시 팝업만 토글 (펜 모드 유지)
        const wrapper = document.querySelector('.whiteboardEx');
        if (wrapper) wrapper.classList.toggle('hide-tool-panel');
        return;
      }
      // 그 외 툴(지우개, 선, 화살표, 텍스트 등)은 재클릭 시 커서로 전환
      excalidrawApi.current.setActiveTool({ type: 'selection' });
      setCurrentTool('selection');
      return;
    }
    // 다른 툴로 전환 시 패널 숨김 해제
    const wrapper = document.querySelector('.whiteboardEx');
    if (wrapper) wrapper.classList.remove('hide-tool-panel');
    excalidrawApi.current.setActiveTool({ type });
    setCurrentTool(type);
  }, [excalidrawApi, setIsOpenDiagramPopover]);

  return {
    currentTool,
    setCurrentTool,
    isOpenDiagramPopover,
    setIsOpenDiagramPopover,
    onClickTools,
  };
}

// ─── useWhiteboardZoom ─────────────────────────────────────────────────────

export function useWhiteboardZoom(
  excalidrawApi: React.RefObject<ExcalidrawAPI | null>,
  initialZoom = 1,
) {
  const [zoomNumber, setZoomNumber] = useState(initialZoom);

  const onClickZoomIn = useCallback(() => {
    excalidrawApi.current?.zoomIn();
  }, [excalidrawApi]);

  const onClickZoomOut = useCallback(() => {
    excalidrawApi.current?.zoomOut();
  }, [excalidrawApi]);

  return { zoomNumber, setZoomNumber, onClickZoomIn, onClickZoomOut };
}

// ─── loadDeferredFiles ─────────────────────────────────────────────────────

const PLACEHOLDER_SIGNATURE =
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

/**
 * Excalidraw 이미지 파일 지연 로딩
 * v1 원본: useWhiteboardCommon.js → loadDeferredFiles()
 */
export async function loadDeferredFiles(
  fileList: Array<{ id: string; thumbnailURL: string; dataURL?: string | null; [k: string]: unknown }>,
  excalidrawApiCurrent: ExcalidrawAPI | null,
  setDrawFileList?: (files: unknown[]) => void,
): Promise<void> {
  if (!excalidrawApiCurrent || !fileList?.length) return;

  const currentFiles = excalidrawApiCurrent.getFiles();
  const filesToLoad = fileList.filter((v) => {
    const existing = currentFiles[v.id];
    if (!existing || !existing.dataURL) return true;
    return existing.dataURL.includes(PLACEHOLDER_SIGNATURE);
  });

  if (filesToLoad.length === 0) return;

  const { convertUrlToBase64 } = await import('@/utils/whiteboard/baseUtils');

  const loadedFiles = await Promise.all(
    filesToLoad.map(async (v) => {
      if (v.dataURL) {
        return { ...v, dataURL: v.dataURL };
      }

      // v1 원본: thumbnailURL.replace("http://dev.api0.wehago.com/", "https://rtctest.wehago.com/api0/")
      const url = v.thumbnailURL.replace(
        'http://dev.api0.wehago.com/',
        'https://rtctest.wehago.com/api0/',
      );
      const base64DataURL = await convertUrlToBase64(url);
      return { ...v, dataURL: base64DataURL };
    }),
  );

  excalidrawApiCurrent.addFiles(loadedFiles);
  setDrawFileList?.(loadedFiles);
}
